import { Box, Button, ButtonGroup, Divider, MenuItem, Menu, Tooltip } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

// components
import { useSearchParams } from 'react-router-dom'
import { AppContext } from '../../../context'
import { GRAPHQL_QUERY_POLICY, QUERY_PARAMS } from '../../../constants'
import { ActionType } from '../../../context/AppContextReducer'
import { ContentHead } from '../../common/ContentHead'
import { CardBox } from '../../../theme/styleComponents'
import { flexCenter } from '../../../theme/styleConstants'
import { EmptyData } from '../../common/EmptyData'

import BankReconciliationListing from './BankReconciliationListing'
import BankReconciliationFilters from './BankReconciliationFilters'
import { useReconciliationCsvExport } from './ExportReconciliationCsv'
import ReconciliationSessionsMenu from './ReconciliationSessionsMenu'
import SessionStatusBar from './SessionStatusBar'
import { SessionItem, SessionViewPreferences, useReconciliationSession } from './useReconciliationSession'
import {
  FetchAccountReconciliationQuery,
  OrderDirection,
  OrderPaymentBy,
  PaymentMethod,
  PaymentType,
  ReconciliationSessionSummaryFragment,
  ReconciliationSessionViewPreferencesInput,
  useFetchAccountReconciliationLazyQuery,
  useFetchAllPaymentsForReconciliationLazyQuery,
  useFetchPaymentsByProfitCenterLazyQuery,
  useMarkPaymentsAsLegacyReconciledMutation
} from '../../../../generated'
import { Alert } from '../../common/Alert'
import { PaymentConciliationData } from '../../../interfaceTypes/props'
import { OverlayLoader } from '../../common/OverlayLoader'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import dayjs from 'dayjs'
import { SearchWithKeypress } from '../../common/SearchWithKeypress'
import ReconciliationsSortFilter from './SortFilter'
import PaymentMethodFilter from './PaymentMethodFilter'
import { useDebounce } from 'use-debounce'

/** Sort options the listing can actually order by. A value outside this set (a stale bookmark, a hand-edited
 * URL) is treated as "unsorted" and replaced with the default, so it neither reaches the listing query nor
 * gets offered to the session — the server drops what it doesn't recognise, and a value it keeps rejecting
 * would have this screen re-saving it forever. */
const ORDER_BY_VALUES = new Set<string>(Object.values(OrderPaymentBy))

/** Sorts a DAY-GROUPED row can be ordered by. The other options describe a single payment (its cheque number,
 * method, profit centre), which a whole day's group has none of, so the grouped view offers only these two and
 * falls back to Date for anything else — without rewriting the param, so the line-item choice survives a toggle. */
const GROUPED_ORDER_BY_VALUES = new Set<string>([OrderPaymentBy.Date, OrderPaymentBy.Amount])

/** How long a run of grouped-view ticks is allowed to coalesce before the session is saved (see below). */
const GROUPED_SELECTION_FLUSH_MS = 300

/** Debounce on persisting the list view/sort settings, so dragging through sort options isn't one save each. */
const VIEW_PREFERENCES_SAVE_MS = 600

/** Put a resumed session's list view/sort settings back into the URL the filters read. A setting the session
 * recorded as empty means "no filter", so the param is removed rather than left at whatever was on screen. */
const applyViewPreferencesToParams = (params: URLSearchParams, preferences: SessionViewPreferences) => {
  const apply = (key: QUERY_PARAMS, value?: string | null) => (value ? params.set(key, value) : params.delete(key))
  if (preferences.lineItems !== null && preferences.lineItems !== undefined) {
    params.set(QUERY_PARAMS.RECONCILIATION_LINE_ITEMS, String(preferences.lineItems))
  }
  apply(QUERY_PARAMS.ORDER_BY, preferences.orderBy)
  apply(QUERY_PARAMS.ORDER_DIRECTION, preferences.orderDirection)
  apply(QUERY_PARAMS.RECONCILIATION_PAYMENT_METHOD, preferences.paymentMethod)
  apply(QUERY_PARAMS.OFFICE, preferences.office)
}

const samePreference = (a?: string | null, b?: string | null) => (a || '') === (b || '')

/** True when the session already stores exactly what the screen is showing — i.e. there is nothing to save. */
const sameViewPreferences = (
  stored: SessionViewPreferences | null,
  current: ReconciliationSessionViewPreferencesInput
): boolean =>
  !!stored &&
  (stored.lineItems ?? true) === current.lineItems &&
  samePreference(stored.orderBy, current.orderBy) &&
  samePreference(stored.orderDirection, current.orderDirection) &&
  samePreference(stored.paymentMethod, current.paymentMethod) &&
  samePreference(stored.office, current.office)

const BankReconciliation = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { searchValue, currentPage, dispatch } = useContext(AppContext)

  const accountId = searchParams.get(QUERY_PARAMS.ACCOUNT_ID)
  const statementMonth = dayjs(searchParams.get(QUERY_PARAMS.STATEMENT_MONTH) || Date()).format('YYYY-MM-DD')

  const [paymentsByDate, setPaymentsByDate] = useState<PaymentConciliationData[]>([])
  const [paymentIdsToReconcile, setPaymentIdsToReconcile] = useState<Set<string>>(new Set())
  // Amount/type of each selected line-item payment, so the credit/debit totals stay correct across Type filter
  // switches (a selected credit is not visible while filtering Debit, but its total must still count).
  const [selectedDetails, setSelectedDetails] = useState<Record<string, { amount: number; paymentType: string }>>({})
  const [loading, setLoading] = useState(false)
  const [showReconcileConfirmation, setShowReconcileConfirmation] = useState(false)
  const [showLegacyReconcileConfirmation, setShowLegacyReconcileConfirmation] = useState(false)
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false)
  const [splitMenuOpen, setSplitMenuOpen] = useState(false)
  const splitAnchorRef = useRef<HTMLDivElement>(null)
  const [showRevertedBanner, setShowRevertedBanner] = useState(false)
  const revertedShownRef = useRef(false)
  // Ids sent to "Mark already reconciled in AL", so the completion handler unselects exactly those.
  const markedIdsRef = useRef<string[]>([])
  const [accountReconciledData, setAccountReconciledData] =
    useState<FetchAccountReconciliationQuery['fetchAccountReconciliation']>()
  const location = searchParams.get(QUERY_PARAMS.OFFICE)
  // Line Item Detail (per-payment rows) vs the day-grouped view. Absent param means ON — the same default the
  // checkbox and the Excel export already use. They used to disagree (`=== 'true'` here, `!== 'false'` there),
  // so on a fresh arrival the box rendered ticked over a grouped listing, with the sort controls missing.
  const isLineItemView = searchParams.get(QUERY_PARAMS.RECONCILIATION_LINE_ITEMS) !== 'false'
  const transactionType = searchParams.get(QUERY_PARAMS.TRANSACTION_TYPE)
  const paymentMethodFilter = searchParams.get(QUERY_PARAMS.RECONCILIATION_PAYMENT_METHOD)
  const reconciled = searchParams.get(QUERY_PARAMS.RECONCILIATION_STATUS)
  // Statement balance is session-owned local state (NOT a query param) — keeping it in the URL round-tripped
  // field→URL→field on every keystroke, causing constant re-renders. The filter reports edits up via a callback.
  const [statementBalance, setStatementBalance] = useState(0)
  const [debouncedSearchValue] = useDebounce(searchValue, 500)
  const orderByParam = searchParams.get(QUERY_PARAMS.ORDER_BY)
  const orderBy = ORDER_BY_VALUES.has(orderByParam ?? '') ? (orderByParam as OrderPaymentBy) : OrderPaymentBy.Date
  const orderDirection = searchParams.get(QUERY_PARAMS.ORDER_DIRECTION) === 'DESC' ? 'DESC' : 'ASC'

  // Live snapshots the session hook reads at save time (avoids stale closures in the debounced save).
  const currentItemsRef = useRef<Map<string, SessionItem>>(new Map())
  const currentBalanceRef = useRef<number | null>(null)
  currentBalanceRef.current = statementBalance || null

  // Load a session's items + balance into local state, and its month + list view/sort settings into the URL.
  const onHydrate = useCallback(
    (items: SessionItem[], balance: number | null, month: string, preferences: SessionViewPreferences | null) => {
      setPaymentIdsToReconcile(new Set(items.map(it => it.paymentId)))
      setSelectedDetails(
        Object.fromEntries(items.map(it => [it.paymentId, { amount: it.amount, paymentType: it.paymentType }]))
      )
      setStatementBalance(balance ?? 0)
      // Base this on the LIVE url: it runs after an async fetch, so the params from the render that created this
      // callback (and the ones react-router would hand a functional update) can be stale — the sessions menu sets
      // the account right before this, and writing a stale snapshot back would undo it.
      setSearchParams(
        () => {
          const params = new URLSearchParams(window.location.search)
          params.set(QUERY_PARAMS.STATEMENT_MONTH, dayjs(month).format('YYYY-MM-DD'))
          // Restore the view in the SAME write as the month so the two can't overwrite each other. Only for a
          // session that actually recorded settings — one created before they were stored keeps what's on screen.
          if (preferences) applyViewPreferencesToParams(params, preferences)
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const onResetLocal = useCallback(() => {
    setPaymentIdsToReconcile(new Set())
    setSelectedDetails({})
    setStatementBalance(0)
  }, [])

  /** Unselect specific payments (e.g. ones that just stopped being reconcilable) without touching the rest. */
  const dropFromSelection = useCallback((ids: string[]) => {
    if (!ids.length) return
    setPaymentIdsToReconcile(prev => {
      const next = new Set(prev)
      ids.forEach(id => next.delete(id))
      return next
    })
    setSelectedDetails(prev => {
      const next = { ...prev }
      ids.forEach(id => delete next[id])
      return next
    })
  }, [])

  const session = useReconciliationSession({
    accountId,
    statementMonth: dayjs(statementMonth).startOf('month').format('YYYY-MM-DD'),
    currentItemsRef,
    currentBalanceRef,
    onHydrate,
    onResetLocal
  })

  const { exportCsv } = useReconciliationCsvExport()

  // An open session for the bank on screen that we are NOT currently in — i.e. the user left it. (On arrival
  // the hook auto-enters any open session for the account, so this state only exists after leaving.)
  // Suppressed while hydrating: the sessions list arrives before the session itself is entered, so without
  // this the screen briefly claims "you've left this session" on every arrival, mid-load.
  const leftSession = useMemo(
    () =>
      accountId && !session.hasSession && !session.hydrating
        ? session.sessions.find(s => s.account?.id === accountId)
        : undefined,
    [accountId, session.hasSession, session.hydrating, session.sessions]
  )

  const [fetchGroupedByDatePayments, { loading: groupedPaymentsLoading, refetch: refetchGroupedPayments }] =
    useFetchPaymentsByProfitCenterLazyQuery({
      ...GRAPHQL_QUERY_POLICY,
      variables: undefined,
      onError(error) {
        Alert.error(error.message)
      },
      onCompleted(data) {
        const { fetchPaymentsByProfitCenter } = data || {}
        const { payments } = fetchPaymentsByProfitCenter || {}
        setPaymentsByDate((payments || []) as PaymentConciliationData[])

        dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: 1 })
        dispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: 1 })
      }
    })

  const [fetchAllPaymentsForReconciliation, { refetch: refetchPayments }] =
    useFetchAllPaymentsForReconciliationLazyQuery({
      ...GRAPHQL_QUERY_POLICY,
      variables: undefined,
      onError(error) {
        Alert.error(error.message)
        setLoading(false)
      },
      onCompleted(data) {
        setTimeout(() => {
          const { fetchAllPaymentsForReconciliation } = data || {}
          const { payments, pagination } = fetchAllPaymentsForReconciliation || {}
          const _date = payments?.map(payment => ({
            ...payment,
            officeName: payment.office?.name,
            officeId: payment.office?.id,
            reconciliationId: payment.reconciliation?.id ?? null
          }))
          setPaymentsByDate(_date || [])

          // Inform (once per account/month) that payments from a reverted reconciliation are present; the user
          // selects them manually — the session persists whatever they choose.
          const hasReverted = (_date || []).some(p => p.id && p.reconciliationId && !p.reconciledAt)
          if (hasReverted && !revertedShownRef.current) {
            revertedShownRef.current = true
            setShowRevertedBanner(true)
          }

          if (pagination) {
            const { totalPages, totalCount } = pagination
            dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages || 1 })
            dispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount || 1 })
          }
          setLoading(false)
        }, 0)
      }
    })

  const [markAsLegacyReconciled, { loading: legacyReconcileLoading }] = useMarkPaymentsAsLegacyReconciledMutation({
    onError: e => Alert.error(e.message),
    async onCompleted(data) {
      if (data?.markPaymentsAsLegacyReconciled?.response?.status === 200) {
        // Only the payments just marked leave the selection — the rest of the draft must survive. They also
        // vanish from the listing, so leaving them selected would strand rows the user can't see or uncheck.
        // The API already pruned them from the session (which bumps its version), so an active session is
        // re-hydrated to pick up the server's set + version; without one, just drop them locally.
        dropFromSelection(markedIdsRef.current)
        markedIdsRef.current = []
        // No-ops when there's no active session (it checks the live session ref, not this closure's copy).
        await session.reload()
        await refetchAccountReconciliation()
        if (isLineItemView) {
          refetchPayments()
        } else {
          refetchGroupedPayments()
        }
        setShowLegacyReconcileConfirmation(false)
        Alert.success('Payments marked reconciled')
      }
    }
  })

  const [
    fetchAccountReconciliationData,
    { loading: fetchAccountReconciliationLoading, refetch: refetchAccountReconciliation }
  ] = useFetchAccountReconciliationLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { fetchAccountReconciliation } = data || {}
      if (fetchAccountReconciliation) {
        setAccountReconciledData(fetchAccountReconciliation)
      }
    }
  })

  const handleReconcilePayments = async () => {
    if (!statementBalance) {
      Alert.error('Please enter statement balance')
      return
    }
    setShowReconcileConfirmation(false)
    const ok = await session.commit()
    if (ok) {
      await refetchAccountReconciliation()
      if (isLineItemView) {
        refetchPayments()
      } else {
        refetchGroupedPayments()
      }
    }
  }

  const handleStartSession = async () => {
    if (!accountId) {
      Alert.error('Please select an account')
      return
    }
    // A draft already exists for this bank and we're just not in it (the user left). Resume it — starting
    // would call startOrResume, which returns that same draft and then flushes whatever is ticked right now
    // into it, silently merging selections made outside the session.
    if (leftSession) {
      await session.enterSession(leftSession.id)
      return
    }
    await session.startSession()
  }

  const handleDiscardSession = async () => {
    setShowDiscardConfirmation(false)
    await session.discard()
  }

  const getReconciliationConfirmationMessage = () => {
    return `Are you sure you want to reconcile ${paymentIdsToReconcile.size} payment(s)? Payments not selected in this reconciliation will appear in the next reconciliation.`
  }

  useEffect(() => {
    if (accountId && statementMonth) {
      fetchAccountReconciliationData({
        variables: {
          fetchAccountReconciliationInput: {
            accountId,
            monthOfYear: dayjs(statementMonth).startOf('month').format('YYYY-MM-DD')
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, statementMonth])

  useEffect(() => {
    if (!accountId) return

    if (isLineItemView) {
      setLoading(true)
      fetchAllPaymentsForReconciliation({
        variables: {
          fetchAllPaymentsInput: {
            keyword: debouncedSearchValue,
            paginationOptions: {
              page: currentPage,
              limit: 3000
            },
            filters: {
              settled: true,
              monthOfYear: dayjs(statementMonth).startOf('month').format('YYYY-MM-DD'),
              accountId: accountId,
              location: location,
              ...(!!transactionType && { paymentType: transactionType as PaymentType }),
              ...(!!paymentMethodFilter && { paymentMethod: paymentMethodFilter as PaymentMethod }),
              ...(!!reconciled && { reconciled: reconciled === 'true' }),
              orderBy,
              orderDirection
            } as any
          }
        }
      })
    } else {
      fetchGroupedByDatePayments({
        variables: {
          input: {
            monthOfYear: dayjs(statementMonth).startOf('month').format('YYYY-MM-DD'),
            accountId: accountId,
            reconciled: false,
            ...(!!transactionType && { paymentType: transactionType as PaymentType }),
            // The same filters and sort the line-item view honours: they narrow the payments BEFORE they are
            // grouped, so a day then totals only the profit centre / payment method asked for.
            ...(!!paymentMethodFilter && { paymentMethod: paymentMethodFilter as PaymentMethod }),
            ...(!!location && { location }),
            orderBy: GROUPED_ORDER_BY_VALUES.has(orderBy) ? orderBy : OrderPaymentBy.Date,
            orderDirection: orderDirection as OrderDirection
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    accountId,
    isLineItemView,
    statementMonth,
    transactionType,
    reconciled,
    orderBy,
    orderDirection,
    debouncedSearchValue,
    location,
    paymentMethodFilter
  ])

  // Keep the per-payment details in sync with the selection in line-item mode: record amount/type from the
  // rows currently loaded and preserve details for selections made under other filters that aren't on screen.
  useEffect(() => {
    if (!isLineItemView) return
    setSelectedDetails(prev => {
      const loaded = new Map<string, { amount: number; paymentType: string }>(
        paymentsByDate
          .filter(p => p.id)
          .map(p => [p.id as string, { amount: p.amount, paymentType: String(p.paymentType) }])
      )
      const next: Record<string, { amount: number; paymentType: string }> = {}
      paymentIdsToReconcile.forEach(id => {
        // Left OUT when neither the loaded rows nor the previous details know this payment (e.g. it was ticked
        // as part of a day-group, which carries no per-payment amount). A placeholder here would be
        // indistinguishable from a real $0 credit and would silently understate the totals; a missing entry
        // makes the screen fall back to the session's server-side totals instead.
        const detail = loaded.get(id) ?? prev[id]
        if (detail) next[id] = detail
      })
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIdsToReconcile, paymentsByDate, isLineItemView])

  // Mirror the current selection into a ref (source for the session's delta save). Runs before markDirty.
  useEffect(() => {
    const map = new Map<string, SessionItem>()
    paymentIdsToReconcile.forEach(id => {
      const d = selectedDetails[id]
      map.set(id, { paymentId: id, amount: d?.amount ?? 0, paymentType: d?.paymentType ?? 'CREDIT' })
    })
    currentItemsRef.current = map
  }, [paymentIdsToReconcile, selectedDetails])

  // Schedule a debounced session save whenever the selection or balance changes (no-op right after hydration
  // because the delta is empty). Month is NOT autosaved — a switch is confirmed via the banner below.
  useEffect(() => {
    session.markDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIdsToReconcile, selectedDetails, statementBalance])

  // Flush pending changes immediately when the Type filter switches, so the session's server-side credit/debit
  // totals are current if the totals widget falls back to them.
  useEffect(() => {
    session.flush()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionType])

  // The grouped query has no keyword search (and the box is hidden there), so a term left over from the
  // line-item view would sit in state, invisible and unapplied. Clear it when the view switches.
  useEffect(() => {
    if (!isLineItemView && searchValue) dispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLineItemView])

  // Grouped view: ticking a day-group tells the screen nothing about the individual payments, so the totals
  // come from the session's server-side figures — which only move when a save lands. Waiting out the 2s
  // autosave would leave the numbers visibly trailing the ticks, so flush promptly instead. Short debounce so
  // a run of ticks still coalesces into one save; line-item mode is left on the normal autosave (its totals
  // are already exact locally, and a select-all there is thousands of items).
  useEffect(() => {
    if (isLineItemView || !session.hasSession) return
    const timer = setTimeout(() => session.flush(), GROUPED_SELECTION_FLUSH_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIdsToReconcile, isLineItemView, session.hasSession])

  // Credit/debit totals of the WHOLE selection. Deliberately independent of the Type filter, the view mode and
  // which rows happen to be loaded: these are the figures the user reconciles against, so they must not move
  // when the screen is merely looked at differently.
  const { creditTotal, debitTotal, amountToReconcile } = useMemo(() => {
    const totals = (credit: number, debit: number) => ({
      creditTotal: credit,
      debitTotal: debit,
      amountToReconcile: credit - debit
    })

    const selectedIds = Array.from(paymentIdsToReconcile)
    if (!selectedIds.length) return totals(0, 0)

    // Best case: the screen holds the amount + type of every selected transaction (line-item rows, plus
    // everything hydrated from the session), so it can total them exactly — and instantly, before the autosave.
    const details = selectedIds.map(id => selectedDetails[id]).filter(Boolean)
    if (details.length === selectedIds.length) {
      const credit = details.reduce((sum, d) => (d.paymentType === 'CREDIT' ? sum + d.amount : sum), 0)
      const debit = details.reduce((sum, d) => (d.paymentType === 'DEBIT' ? sum + d.amount : sum), 0)
      return totals(credit, debit)
    }

    // Something selected has no per-payment detail on screen — a day-group ticked in the grouped view only
    // ever yields the group's NET, which can't be split per side (a day holding both a deposit and a cheque
    // nets them out) and can't be attributed at all when the group is only partly selected. The session's
    // server-side totals cover the entire selection, so use those: the very numbers the commit validates.
    if (session.hasSession) return totals(session.sessionCreditTotal, session.sessionDebitTotal)

    // No session to fall back on (just browsing): total the GROSS credit/debit of the fully-selected groups.
    // Outside a session every tick covers a whole group, so this is exact.
    const fullySelected = paymentsByDate.filter(
      g => g.paymentIds?.length && g.paymentIds.every(id => paymentIdsToReconcile.has(id))
    )
    const credit = fullySelected.reduce((sum, g) => sum + Number(g.creditAmount ?? (g.amount > 0 ? g.amount : 0)), 0)
    const debit = fullySelected.reduce((sum, g) => sum + Number(g.debitAmount ?? (g.amount < 0 ? -g.amount : 0)), 0)
    return totals(credit, debit)
  }, [
    paymentsByDate,
    paymentIdsToReconcile,
    selectedDetails,
    session.hasSession,
    session.sessionCreditTotal,
    session.sessionDebitTotal
  ])

  const isEmpty = paymentsByDate?.length === 0

  useEffect(() => {
    setShowRevertedBanner(false)
    revertedShownRef.current = false
  }, [accountId, statementMonth])

  const handleSelectSession = (picked: ReconciliationSessionSummaryFragment) => {
    const sameBank = picked.account.id === accountId
    setSearchParams(() => {
      const params = new URLSearchParams(window.location.search)
      params.set(QUERY_PARAMS.ACCOUNT_ID, picked.account.id)
      params.set(QUERY_PARAMS.STATEMENT_MONTH, dayjs(picked.statementMonth).format('YYYY-MM-DD'))
      return params
    })
    // Switching bank is enough on its own — the hook's resolve effect keys on the account and will load the
    // session. Resuming one for the bank already on screen (i.e. after leaving it) changes no account, so
    // nothing would fire; enter it explicitly.
    if (sameBank) session.enterSession(picked.id)
  }

  // The active session is tied to a specific statement month. If the user switches the viewed month, the filters
  // component prompts (below the Statement picker) before moving the session — the month is never changed
  // silently (it determines the committed reconciliation's period). Confirm persists the new month; Cancel (in
  // the child) snaps the Statement picker back to the session's month.
  const viewedMonth = dayjs(statementMonth).startOf('month').format('YYYY-MM-DD')
  // Suppress while hydrating so switching sessions (account + month change together) doesn't flash the prompt
  // before the incoming session's month is loaded; an in-place month change doesn't hydrate, so it still shows.
  const monthMismatch =
    session.hasSession && !session.hydrating && !!session.sessionMonth && viewedMonth !== session.sessionMonth

  const handleConfirmMonthChange = () => {
    session.changeMonth(viewedMonth)
  }

  // How the list is currently set up. Only the settings that belong to the reconciliation itself — the Type
  // and Status filters are left out deliberately: they change what is on screen rather than how it is shown,
  // and resuming into a filtered list would hide part of the draft.
  const viewPreferences = useMemo<ReconciliationSessionViewPreferencesInput>(
    () => ({
      lineItems: isLineItemView,
      orderBy,
      orderDirection,
      paymentMethod: (paymentMethodFilter as PaymentMethod) || null,
      office: location || null
    }),
    [isLineItemView, orderBy, orderDirection, paymentMethodFilter, location]
  )

  // Persist that on the session, so resuming days later puts the list back — including the search/sort row,
  // which only exists in line-item mode and used to disappear whenever the URL params were lost. Skipped
  // while the session is loading or mid-action (the settings are still moving), and skipped when the session
  // already stores them, so re-applying them on hydrate can't bounce straight back as a save.
  useEffect(() => {
    if (!session.hasSession || session.hydrating || session.busy) return
    if (sameViewPreferences(session.sessionPreferences, viewPreferences)) return
    const timer = setTimeout(() => session.savePreferences(viewPreferences), VIEW_PREFERENCES_SAVE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPreferences, session.hasSession, session.hydrating, session.busy, session.sessionPreferences])

  return (
    <>
      <CardBox marginTop={3}>
        <ContentHead
          title={'Bank Reconciliation'}
          customButton={() => (
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
              <ReconciliationSessionsMenu
                sessions={session.sessions}
                activeSessionId={session.sessionId}
                hasActiveSession={session.hasSession}
                canStart={!!accountId}
                canResume={!!leftSession}
                saveStatus={session.saveStatus}
                onOpen={async () => {
                  await session.flush()
                  session.refreshSessions()
                }}
                onSelect={handleSelectSession}
                onStart={handleStartSession}
                onSaveNow={() => session.flush()}
                onLeave={() => session.leaveSession()}
                onDiscard={() => setShowDiscardConfirmation(true)}
              />
              <ButtonGroup ref={splitAnchorRef} variant="contained">
                <Button
                  onClick={() => setShowReconcileConfirmation(true)}
                  disabled={!session.hasSession || paymentIdsToReconcile.size === 0}
                >
                  Reconcile Payments
                </Button>
                <Button size="small" onClick={() => setSplitMenuOpen(prev => !prev)} sx={{ px: 0.5 }}>
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>

              <Menu
                anchorEl={splitAnchorRef.current}
                open={splitMenuOpen}
                onClose={() => setSplitMenuOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  disabled={!session.hasSession || paymentIdsToReconcile.size === 0}
                  onClick={() => {
                    setSplitMenuOpen(false)
                    setShowReconcileConfirmation(true)
                  }}
                >
                  Reconcile Payments
                </MenuItem>
                <MenuItem
                  disabled={paymentIdsToReconcile.size === 0}
                  onClick={() => {
                    setSplitMenuOpen(false)
                    exportCsv(Array.from(paymentIdsToReconcile))
                  }}
                >
                  Export to Excel
                </MenuItem>
                {/* This marks EVERY selected transaction, so inside a session it would sweep up the whole
                    draft — there's only one selection on the screen. Leave the session (it stays open and
                    resumable) to tick just the handful that ADV already cleared. */}
                <Tooltip
                  title={
                    session.hasSession
                      ? 'Not available while working in a session — it would mark every transaction in your draft. Leave the session from the Sessions menu first; it stays saved and resumable.'
                      : ''
                  }
                  placement="left"
                >
                  <span>
                    <MenuItem
                      disabled={session.hasSession || paymentIdsToReconcile.size === 0}
                      onClick={() => {
                        setSplitMenuOpen(false)
                        setShowLegacyReconcileConfirmation(true)
                      }}
                    >
                      Mark already reconciled in AL
                    </MenuItem>
                  </span>
                </Tooltip>
              </Menu>
            </Box>
          )}
        />

        <SessionStatusBar
          hasSession={session.hasSession}
          accountName={session.sessionAccountName}
          accountSortCode={session.sessionAccountSortCode}
          statementMonth={session.sessionMonth}
          selectedCount={paymentIdsToReconcile.size}
          saveStatus={session.saveStatus}
          pausedSession={
            leftSession
              ? { statementMonth: leftSession.statementMonth, itemCount: leftSession.itemCount }
              : undefined
          }
          onResume={leftSession ? () => session.enterSession(leftSession.id) : undefined}
        />

        <Box display={'flex'} flex={1} justifyContent={'space-between'} alignItems={'center'} m={2}>
          <BankReconciliationFilters
            amountToReconcile={amountToReconcile}
            creditTotal={creditTotal}
            debitTotal={debitTotal}
            accountReconciledData={accountReconciledData}
            statementBalance={statementBalance}
            onStatementBalanceChange={setStatementBalance}
            showMonthChangePrompt={monthMismatch}
            sessionMonth={session.sessionMonth}
            sessionAccountName={session.sessionAccountName}
            monthChangeBusy={session.busy}
            onConfirmMonthChange={handleConfirmMonthChange}
          />
        </Box>

        <Divider />

        {/* Filtering and sorting apply to BOTH views — the day-grouped view needs them just as much, to pull
            up one profit centre's transactions or to order a month by amount. Only keyword search is
            line-item-only: a cheque or client number cannot single out a whole day's net. */}
        <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} m={2} gap={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={2}>
            {isLineItemView && <SearchWithKeypress searchPlaceholder={'Check#/Client#/Vendor#/Bill#'} />}
            <PaymentMethodFilter />
          </Box>
          <ReconciliationsSortFilter />
        </Box>

        {isEmpty ? (
          <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
            <EmptyData title={'No Bank Reconciliations Recorded!'} />
          </Box>
        ) : (
          <BankReconciliationListing
            setPaymentIdsToReconcile={setPaymentIdsToReconcile}
            paymentIdsToReconcile={paymentIdsToReconcile}
            payments={paymentsByDate}
            loading={groupedPaymentsLoading || loading}
            refetch={isLineItemView ? refetchPayments : refetchGroupedPayments}
          />
        )}

        {(fetchAccountReconciliationLoading || session.busy || session.hydrating || legacyReconcileLoading) && (
          <OverlayLoader />
        )}

        <ConfirmationModal
          isOpen={showReconcileConfirmation}
          title="Confirm Reconciliation"
          message={getReconciliationConfirmationMessage()}
          confirmText="Finish Reconciliation"
          cancelText="Cancel"
          variant="danger"
          isLoading={session.busy}
          onConfirm={handleReconcilePayments}
          onCancel={() => setShowReconcileConfirmation(false)}
        />

        <ConfirmationModal
          isOpen={showLegacyReconcileConfirmation}
          title="Mark as Already Reconciled"
          message={`Mark ${paymentIdsToReconcile.size} payment(s) reconciled as cleared in Advantage Law? This will only mark payment reconciled, no balances or history will be updated. This action cannot be undone.`}
          confirmText="Confirm"
          cancelText="Cancel"
          variant="danger"
          isLoading={legacyReconcileLoading}
          onConfirm={() => {
            const paymentIds = Array.from(paymentIdsToReconcile)
            markedIdsRef.current = paymentIds
            markAsLegacyReconciled({ variables: { input: { paymentIds } } })
          }}
          onCancel={() => setShowLegacyReconcileConfirmation(false)}
        />

        <ConfirmationModal
          isOpen={showDiscardConfirmation}
          title="Discard Session"
          message="This discards the current reconciliation session and everything selected in it. This cannot be undone. Continue?"
          confirmText="Discard Session"
          cancelText="Cancel"
          variant="danger"
          isLoading={session.busy}
          onConfirm={handleDiscardSession}
          onCancel={() => setShowDiscardConfirmation(false)}
        />
      </CardBox>
    </>
  )
}

export default BankReconciliation
